import {ellipsis} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/core/src/helpers/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {AnalyticsReportsEnum} from '../../helpers/reportHelpers/reportTypes';
import {ReportNavigationLink} from './reportNavigationLink';

/*
 * Constants.
 */

export const analyticsLinksConfig: ReadonlyArray<LinkConfig> = [
  {
    type: AnalyticsReportsEnum.OVERVIEW,
    iconName: 'globeSmall',
    selectedIconName: 'globeFilledSmall',
    name: <Trans id="analytics-link-overview">Overview</Trans>,
  },
  {
    type: AnalyticsReportsEnum.TEAM_PERF,
    iconName: 'teammatesSmall',
    selectedIconName: 'teammatesFilledSmall',
    name: <Trans id="analytics-link-team_performance">Team Performance</Trans>,
  },
  {
    type: AnalyticsReportsEnum.CONVERSATIONS,
    iconName: 'channelChatOutlineSmall',
    selectedIconName: 'channelChatSmall',
    name: <Trans id="analytics-link-conversations">Conversations</Trans>,
  },
  {
    type: AnalyticsReportsEnum.TAGS,
    iconName: 'tagsSmall',
    selectedIconName: 'tagsFilledSmall',
    name: <Trans id="analytics-link-tags">Tags</Trans>,
  },
  {
    type: AnalyticsReportsEnum.MESSAGE_TEMPLATES,
    iconName: 'messageTemplateSmall',
    selectedIconName: 'messageTemplateFilledSmall',
    name: <Trans id="analytics-link-message-templates">Message Templates</Trans>,
  },
  {
    type: AnalyticsReportsEnum.SLA,
    iconName: 'slaSmall',
    selectedIconName: 'slaFilledSmall',
    name: <Trans id="analytics-link-sla">SLAs</Trans>,
  },
  {
    type: AnalyticsReportsEnum.CUSTOMER_EXPERIENCE,
    iconName: 'csatSmall',
    selectedIconName: 'csatFilledSmall',
    name: <Trans id="analytics-link-customer_experience">Customer Satisfaction</Trans>,
  },
];

/*
 * Styles.
 */

export const StyledAnalyticsNavigationTitleDiv = styled.div`
  padding: 6px 8px 6px 14px;
  min-width: 0;

  line-height: normal;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};

  ${ellipsis()};

  color: ${(p) => p.theme.header};
`;

/*
 * Props.
 */

type LinkConfig = {
  type: AnalyticsReportsEnum;
  iconName: IconName;
  selectedIconName: IconName;
  name: ReactNode;
};

interface NavigationLinkProps {
  isDisabled: boolean;
}

/*
 * Component.
 */

export const OldReportNavigationLinks: FC<NavigationLinkProps> = ({isDisabled}) => {
  const interactionId = 'analytics-navigation';
  const interactionProps = buildInteractionIdProps(interactionId);

  return (
    <div {...interactionProps}>
      <StyledAnalyticsNavigationTitleDiv>
        <Trans id="analytics-communication-navigation-title">Communication</Trans>
      </StyledAnalyticsNavigationTitleDiv>
      {analyticsLinksConfig.map((link, index) => {
        if (index === 0) {
          return <div key={link.type}>{renderNavigationLink(link, isDisabled)}</div>;
        }

        return <div key={link.type}>{renderNavigationLink(link, isDisabled)}</div>;
      })}
    </div>
  );
};

/*
 * Helpers.
 */

function renderNavigationLink(link: LinkConfig, isDisabled: boolean) {
  return (
    <ReportNavigationLink
      key={link.type}
      reportType={link.type}
      iconName={link.iconName}
      selectedIconName={link.selectedIconName}
      isDisabled={isDisabled}
    >
      {link.name}
    </ReportNavigationLink>
  );
}
